import React, { useEffect, useState, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { Link } from 'react-router-dom';
import 'swiper/swiper-bundle.css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { useGlobal } from '../global/GlobalContext';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import { FiArrowRight, FiCheck } from 'react-icons/fi';
import { cacheManager } from '../utils/cacheManager';
import { useWishlistAddToCart } from '../hooks/useWishlistAddToCart';
import SizeSelectionPopup from './SizeSelectionPopup';
import DistributorQuantityPopup from './DistributorQuantityPopup';
import ProductCard from './ProductCard';
import { UserRole } from '../config/userRole';

// Above this count a row can't fit on one screen even on large desktops
// (matches the largest `slidesPerView` breakpoint below), so it's worth a
// carousel. At or under it, a carousel just leaves short rows pinned to the
// left with a big empty gap on the right — a plain centered row looks better.
const CAROUSEL_THRESHOLD = 6;

const PromoCard = ({ image, heading, description, bullets, buttonText, buttonLink, className = '' }) => (
  <div className={`relative w-full sm:w-auto sm:flex-1 sm:min-w-[300px] rounded-2xl overflow-hidden ${className}`}>
    <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className="relative z-10 h-full flex flex-col justify-center gap-2 p-6 max-w-[60%]">
      <h3 className="text-lg md:text-xl font-bold text-gray-900 leading-snug">{heading}</h3>
      {description && <p className="text-xs text-gray-600">{description}</p>}
      {bullets && (
        <ul className="flex flex-col gap-1.5 mt-1">
          {bullets.map((b) => (
            <li key={b} className="flex items-center gap-2 text-xs font-medium text-gray-800">
              <span className="w-4 h-4 rounded-full bg-brand-600 text-white flex items-center justify-center shrink-0">
                <FiCheck size={10} />
              </span>
              {b}
            </li>
          ))}
        </ul>
      )}
      <Link
        to={buttonLink}
        className="mt-2 inline-flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold px-4 py-2.5 rounded-lg w-fit transition-colors"
      >
        {buttonText} <FiArrowRight size={12} />
      </Link>
    </div>
  </div>
);

const SingleSlider = ({ slug_name, price_filter, title, discountPercentage, cat_slug_name, promo }) => {
  const { token, wishlist, setWishlist } = useGlobal();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const {
    addingId,
    isSizePopupOpen,
    sizeOptions,
    selectedVariation,
    setSelectedVariation,
    closeSizePopup,
    confirmSize,
    isQtyPopupOpen,
    selectedQuantity,
    setSelectedQuantity,
    popupQuantities,
    getItemPackQty,
    closeQtyPopup,
    confirmQuantity,
    handleAddToCartClick,
    currentItem,
  } = useWishlistAddToCart({ navigateOnSuccess: false });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const cachedProducts = cacheManager.get(`fp_${title}`);
      if (cachedProducts) {
        setProducts(cachedProducts);
        setLoading(false);
        return;
      }

      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/${slug_name}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      let filteredProduct = res.data.products || [];

      if (UserRole.userRole === "distributor") {
        filteredProduct = filteredProduct.filter(
          (product) => product.pack_price != null && product.pack_qty != null
        );
      }

      if (price_filter) {
        filteredProduct = filteredProduct.filter(
          (product) => product.sale_price <= price_filter
        );
      }

      if (discountPercentage) {
        filteredProduct = filteredProduct.filter((product) => {
          const regularPrice = parseFloat(product.regular_price || 0);
          const salePrice = parseFloat(product.sale_price || 0);
          if (regularPrice > 0 && salePrice > 0) {
            const calculatedDiscount = ((regularPrice - salePrice) / regularPrice) * 100;
            return calculatedDiscount >= discountPercentage;
          }
          return false;
        });
      }

      if (filteredProduct.length > 0) {
        cacheManager.set(`fp_${title}`, filteredProduct);
        setProducts(filteredProduct);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [slug_name, price_filter, discountPercentage, token, title]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  if (!loading && products.length === 0) return null;

  const showCarousel = loading || products.length >= CAROUSEL_THRESHOLD;

  return (
    <div className="pt-3 max-w-7xl mx-auto px-4">
      <div className="flex justify-between items-end mb-5 md:mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 capitalize">{title}</h2>
          <span className="block w-9 h-1 bg-brand-600 rounded-full mt-1.5" />
        </div>
        {cat_slug_name && (
          <Link
            className="flex items-center gap-1 text-sm font-semibold text-brand-600 hover:underline shrink-0"
            to={`/${cat_slug_name}`}
          >
            View All <FiArrowRight size={14} />
          </Link>
        )}
      </div>

      {promo ? (
        <>
          {/* Desktop: 60% product carousel (always scrollable, regardless of
              product count) + 40% pinned promo banner, side by side. */}
          <div className="hidden md:flex gap-4">
            <div className="w-[60%] h-[400px]">
              <Swiper
                navigation
                loop={false}
                breakpoints={{
                  768: { slidesPerView: 2 },
                  1024: { slidesPerView: 2.5 },
                  1280: { slidesPerView: 3 },
                }}
                spaceBetween={16}
                modules={[Navigation, Pagination, Autoplay]}
                className="mySwiper h-full !pb-2"
              >
                {loading && products.length === 0 ? (
                  [...Array(3)].map((_, idx) => (
                    <SwiperSlide key={`skeleton-${idx}`}>
                      <Skeleton height={220} className="rounded-2xl" />
                      <Skeleton height={16} width="80%" className="mt-3" />
                      <Skeleton height={14} width="50%" className="mt-2" />
                    </SwiperSlide>
                  ))
                ) : (
                  products.map((product) => (
                    <SwiperSlide key={product.id}>
                      <ProductCard
                        product={product}
                        wishlist={wishlist}
                        setWishlist={setWishlist}
                        token={token}
                        onQuickAdd={handleAddToCartClick}
                        addingId={addingId}
                      />
                    </SwiperSlide>
                  ))
                )}
              </Swiper>
            </div>
            <div className="w-[40%] h-[400px]">
              <PromoCard {...promo} className="!w-full h-full" />
            </div>
          </div>

          {/* Mobile: swipeable product carousel, promo banner full-width below */}
          <div className="md:hidden">
            <Swiper
              navigation={false}
              loop={false}
              breakpoints={{
                0: { slidesPerView: 2.1 },
                480: { slidesPerView: 2.6 },
              }}
              spaceBetween={12}
              modules={[Navigation, Pagination, Autoplay]}
              className="mySwiper !pb-2"
            >
              {loading && products.length === 0 ? (
                [...Array(4)].map((_, idx) => (
                  <SwiperSlide key={`skeleton-mobile-${idx}`}>
                    <Skeleton height={180} className="rounded-2xl" />
                    <Skeleton height={14} width="80%" className="mt-3" />
                    <Skeleton height={12} width="50%" className="mt-2" />
                  </SwiperSlide>
                ))
              ) : (
                products.map((product) => (
                  <SwiperSlide key={product.id}>
                    <ProductCard
                      product={product}
                      wishlist={wishlist}
                      setWishlist={setWishlist}
                      token={token}
                      onQuickAdd={handleAddToCartClick}
                      addingId={addingId}
                    />
                  </SwiperSlide>
                ))
              )}
            </Swiper>
            <PromoCard {...promo} className="h-[280px] mt-4" />
          </div>
        </>
      ) : showCarousel ? (
        <Swiper
          navigation
          loop={false}
          breakpoints={{
            360: { slidesPerView: 2.1 },
            640: { slidesPerView: 3.2 },
            768: { slidesPerView: 4 },
            1024: { slidesPerView: 6 },
          }}
          spaceBetween={16}
          modules={[Navigation, Pagination, Autoplay]}
          className="mySwiper !pb-2"
        >
          {loading && products.length === 0 ? (
            [...Array(6)].map((_, idx) => (
              <SwiperSlide key={`skeleton-${idx}`}>
                <Skeleton height={220} className="rounded-2xl" />
                <Skeleton height={16} width="80%" className="mt-3" />
                <Skeleton height={14} width="50%" className="mt-2" />
              </SwiperSlide>
            ))
          ) : (
            products.map((product) => (
              <SwiperSlide key={product.id}>
                <ProductCard
                  product={product}
                  wishlist={wishlist}
                  setWishlist={setWishlist}
                  token={token}
                  onQuickAdd={handleAddToCartClick}
                  addingId={addingId}
                />
              </SwiperSlide>
            ))
          )}
        </Swiper>
      ) : (
        <div className="flex flex-wrap gap-4">
          {products.map((product) => (
            <div key={product.id} className="w-[47%] sm:w-[31%] md:w-[23%] lg:w-[15.5%]">
              <ProductCard
                product={product}
                wishlist={wishlist}
                setWishlist={setWishlist}
                token={token}
                onQuickAdd={handleAddToCartClick}
                addingId={addingId}
              />
            </div>
          ))}
        </div>
      )}

      <SizeSelectionPopup
        open={isSizePopupOpen}
        sizeOptions={sizeOptions}
        selectedVariation={selectedVariation}
        onSelect={setSelectedVariation}
        onClose={closeSizePopup}
        onConfirm={confirmSize}
      />

      <DistributorQuantityPopup
        open={isQtyPopupOpen}
        packQty={getItemPackQty(currentItem, selectedVariation)}
        quantities={popupQuantities}
        selectedQuantity={selectedQuantity}
        onSelect={setSelectedQuantity}
        onClose={closeQtyPopup}
        onConfirm={confirmQuantity}
      />
    </div>
  );
};

export default SingleSlider;
